import { IMeeting, createMeeting, getRoundedValue, getTimeValues, roundUpTime, useViewModel } from '@utils';
import { CalendarViewModel } from '@views';
import { addMinutes, set } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { FC, Fragment, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FilterViewModel } from '../../filter';
import { Meeting } from '../../meeting';
import { MeetingViewModel } from '../views';
import { Col } from './Col';

interface ColsProps {
  isDayCalendar?: boolean;
}

/**
 * @param isDayCalendar true, если это дневной календарь, где одна колонка. Влияет на расстягивание встреч по всей ширине колонки
 */
export const Cols: FC<ColsProps> = observer(({ isDayCalendar }) => {
  const calendarViewModel = useViewModel(CalendarViewModel);
  const meetingViewModel = useViewModel(MeetingViewModel);
  const filterViewModel = useViewModel(FilterViewModel);

  const colsRef = useRef<HTMLDivElement>();
  const timeRef = useRef<HTMLDivElement>();

  // ширина колонки. Из ширины всех колонок вычитается ширина колоки со временем и разность делится на кол-во дней(7 для недельного, 1 для дневного)
  const [colWidth, setColWidth] = useState((colsRef.current?.getBoundingClientRect().width - timeRef.current?.getBoundingClientRect().width) / calendarViewModel.viewCalendar.length);
  // минимальное значение left для встреч. Колонка со временем первая стоит в .cols и после нее могут ставиться встречи
  const [meetingMinLeft, setMeetingMinLeft] = useState(timeRef.current?.getBoundingClientRect().width);
  const coordYBeforeMoving = useRef<number>(-1);

  // при обновлении newMeetingState, значение не подгружается в mouseup(я так поняла, потому что сначала реакт обрабатывает событие mousemove,а затем обновляет стейт). Но актуальное значение из стейта отображается во встрече..
  const [newMeetingState, setNewMeetingState] = useState<IMeeting>();
  // а здесь наоброт. Актуальные даты попадают в mouseup, но не попадают во встречу.. почему?
  const newMeeting = useRef<IMeeting>();

  const dayForNewMeeting = useRef<Date>();
  useEffect(() => {
    updateSizes();
  }, [colsRef.current, timeRef.current]);

  useEffect(() => {
    setTimeout(() => updateSizes(), 250);
  }, [filterViewModel.openPanel]);

  const updateMeetingWidth = () =>
    setColWidth((colsRef.current?.getBoundingClientRect().width - timeRef.current?.getBoundingClientRect().width) / calendarViewModel.viewCalendar.length);

  const updateMeetingLeft = () =>
    setMeetingMinLeft(timeRef.current?.getBoundingClientRect().width + (filterViewModel.openPanel ? 1 : 0));

  const updateSizes = () => {
    updateMeetingWidth();
    updateMeetingLeft();
  };

  useEffect(() => {
    window.addEventListener('resize', updateSizes);
    return () => {
      window.removeEventListener('resize', updateSizes);
      document.removeEventListener('mousemove', mouseMove);
    };
  }, []);

  const getMeeting = (startDate: Date, endDate: Date, currentValue?: IMeeting) => {
    const roundedEndDate = roundUpTime(endDate);

    const roundedTimes = {
      startDate: roundUpTime(startDate),
      endDate: roundedEndDate.getHours() === 0 && roundedEndDate.getMinutes() === 0
        ? set(endDate, { hours: 23, minutes: 59 })
        : roundedEndDate
    };

    return !currentValue
      ? createMeeting(roundedTimes)
      : { ...newMeeting.current, ...roundedTimes };
  };

  const date = new Date();

  const isCanAddNewMeeting = useCallback((colDay: Date, colHour: number) =>
    // нет двигаемых встреч
    !meetingViewModel.draggingMeetingId &&
    // ячейка часа нах-ся в будущем времени
    set(colDay, { hours: colHour }).getTime() >= set(date, { minutes: 0, seconds: 0, milliseconds: 0 }).getTime()
    // и мы не просматриваем КФЗ и встречи другого юзера
    && !calendarViewModel.selectedRoom && !calendarViewModel.selectedUser
    , [meetingViewModel.draggingMeetingId]);

  const onMouseUpDay = useCallback((e: MouseEvent<HTMLDivElement>, day: Date, hour: number) => {
    const getTimes = () => {
      // если позиция курсор зажали и чуть сдвинули(защита от случайного перемещения курсора на пару пикселей), то считается, что это был обычный клик (поэтому встречи выставляется время ячейки, куда кликнули)
      if (e.clientY - coordYBeforeMoving.current <= 15) {
        const startDate = hour === date.getHours()
          ? set(day, { hours: hour, minutes: roundUpTime(date).getMinutes() })
          : set(day, { hours: hour, minutes: 0 });

        const endDate = hour === 23
          ? set(day, { hours: hour, minutes: 59 })
          : set(day, { hours: hour + 1, minutes: 0 });

        return { startDate, endDate };

        // иначе курсором двигали, и выставляется время, в зависимости от положения курсора
      } else {
        return {
          startDate: newMeeting.current?.startDate,
          endDate: newMeeting.current?.endDate
        };
      }
    };

    if (isCanAddNewMeeting(day, hour)) {
      meetingViewModel.setEditable(true);
      const { startDate, endDate } = getTimes();
      meetingViewModel.initMeeting({
        startDate,
        // конец больше начала может быть в том случае, когда мы курсор увели выше, чем начало встречи. В таком случае конец будет считаться от начала + 15 минут(минимальная продолжительность встречи)
        endDate: endDate < startDate ? addMinutes(startDate, 15) : endDate
      });
    };
  }, []);

  const mouseUp = (e: globalThis.MouseEvent) => {
    meetingViewModel.setEditable(true);
    meetingViewModel.initMeeting({
      startDate: newMeeting.current.startDate,
      endDate: newMeeting.current.endDate
    });
    document.removeEventListener('mousemove', mouseMove);
  };

  const mouseMove = (e: globalThis.MouseEvent) => {
    if (!meetingViewModel.draggingMeetingId && coordYBeforeMoving.current !== -1 && e.clientY - coordYBeforeMoving.current > 15) {
      const { startDate, endDate } = getTimeValues(dayForNewMeeting.current, dayForNewMeeting.current, getRoundedValue(coordYBeforeMoving.current - colsRef.current?.getBoundingClientRect().top, 0), getRoundedValue(e.clientY - coordYBeforeMoving.current, 0, true), calendarViewModel.startHour);

      newMeeting.current = getMeeting(startDate, endDate, newMeeting.current);
      setNewMeetingState(prev => getMeeting(startDate, endDate, prev));
    }
  };

  const onMouseDownDay = useCallback((e: MouseEvent<HTMLDivElement>, day: Date, hour: number) => {
    if (isCanAddNewMeeting(day, hour)) {
      !meetingViewModel.draggingMeetingId && (coordYBeforeMoving.current = e.clientY);
      dayForNewMeeting.current = day;

      document.addEventListener('mouseup', mouseUp, { once: true });
      document.addEventListener('mousemove', mouseMove);
    }
  }, [meetingViewModel.draggingMeetingId]);

  const onMouseMoveCol = useCallback((e: MouseEvent<HTMLDivElement>, day: Date) => {
    if (!meetingViewModel.draggingMeetingId
      && coordYBeforeMoving.current !== -1
      && e.clientY - coordYBeforeMoving.current > 15
    ) {
      const { startDate, endDate } = getTimeValues(day, day, getRoundedValue(coordYBeforeMoving.current - colsRef.current.getBoundingClientRect().top, 0), getRoundedValue(e.clientY - coordYBeforeMoving.current, 0, true), calendarViewModel.startHour);

      newMeeting.current = getMeeting(startDate, endDate, newMeeting.current);
      setNewMeetingState(prev => getMeeting(startDate, endDate, prev));
    }
  }, [coordYBeforeMoving.current, meetingViewModel.draggingMeetingId]);

  return (
    <div
      className='cols'
      ref={colsRef}
    >
      <Col
        ref={timeRef}
        hours={calendarViewModel.hours}
      />
      {calendarViewModel.viewCalendar.map((day) =>
        <Fragment key={day.toLocaleDateString()}>
          {!Number.isNaN(colWidth) && calendarViewModel.filteredMeetings.get(day.toDateString())?.map((meeting) =>
            <Meeting
              key={meeting.id + meeting.startDate.getTime()}
              meeting={meeting}
              colWidth={colWidth}
              offsetLeft={meetingMinLeft}
              isShowInitiator={!!calendarViewModel.selectedRoom}
              pageLeft={timeRef.current?.getBoundingClientRect()?.left + timeRef.current?.getBoundingClientRect()?.width}
              isDayCalendar={isDayCalendar}
            />
          )}
          {newMeetingState && !calendarViewModel.selectedRoom && !calendarViewModel.selectedUser &&
            <Meeting
              meeting={newMeetingState}
              colWidth={colWidth}
              offsetLeft={meetingMinLeft}
              pageLeft={timeRef.current?.getBoundingClientRect()?.left + timeRef.current?.getBoundingClientRect()?.width}
              isDayCalendar={isDayCalendar}
              isCreatingMeeting
            />
          }
          <Col
            key={day.toLocaleDateString()}
            day={day}
            hours={calendarViewModel.hours}
            isCanAddMeeting={isCanAddNewMeeting}
            onMouseDownDay={onMouseDownDay}
            onMouseMoveCol={onMouseMoveCol}
            onMouseUpDay={onMouseUpDay}
            isDayCalendar={isDayCalendar}
            workCalendarDay={calendarViewModel.findWorkCalendarDay(day)}
          />
        </Fragment>
      )}
    </div>
  );
});
